import React, { useEffect, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { Download, FileSpreadsheet } from "lucide-react";
import { uniqueRanches } from "../../lib/ranchUtils";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const Stat = ({ label, value, tone }) => (
  <div className="bg-white border border-stone-200 p-5" data-testid={`stat-${label}`}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-stone-500">{label}</div>
    <div className={`text-3xl font-bold mt-1 ${tone || ""}`} style={{ fontFamily: "'Fraunces', serif" }}>{value}</div>
  </div>
);

const Breakdown = ({ title, items }) => (
  <div className="bg-white border border-stone-200 p-4">
    <div className="text-sm font-bold uppercase tracking-wide mb-2">{title}</div>
    <div className="space-y-1 max-h-56 overflow-y-auto">
      {items.length === 0 && <div className="text-xs text-stone-400">No data</div>}
      {items.map((i) => (
        <div key={i.label} className="flex justify-between text-sm border-b border-stone-100 py-1">
          <span className="truncate pr-2">{i.label}</span><span className="font-mono font-bold">{money(i.amount)}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function ExpenseDashboard({ ranches, employees, jobcodes, categories }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [filters, setFilters] = useState({ start_date: monthAgo, end_date: today, ranch_id: "", employee_id: "", job_category: "", vendor: "", expense_category: "", status: "" });
  const [d, setD] = useState(null);
  const jobCats = [...new Set(jobcodes.map((j) => j.category).filter(Boolean))].sort();

  const qs = () => Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const load = async () => { const { data } = await api.get(`/expenses/dashboard?${qs()}`); setD(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const download = async (fmt) => {
    const token = getToken();
    const res = await fetch(`${API}/expenses/export/file?fmt=${fmt}&${qs()}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `expenses.${fmt}`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const S = "h-11 px-2 border border-stone-200 bg-white text-sm";

  return (
    <div className="space-y-5" data-testid="expense-dashboard">
      {/* Filters */}
      <div className="bg-white border border-stone-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        <input data-testid="dash-start" type="date" value={filters.start_date} onChange={(e) => set("start_date", e.target.value)} className={S} />
        <input data-testid="dash-end" type="date" value={filters.end_date} onChange={(e) => set("end_date", e.target.value)} className={S} />
        <select value={filters.ranch_id} onChange={(e) => set("ranch_id", e.target.value)} className={S}><option value="">All Ranches</option>{uniqueRanches(ranches).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <select value={filters.employee_id} onChange={(e) => set("employee_id", e.target.value)} className={S}><option value="">All Employees</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
        <select value={filters.job_category} onChange={(e) => set("job_category", e.target.value)} className={S}><option value="">All Job Cats</option>{jobCats.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={filters.expense_category} onChange={(e) => set("expense_category", e.target.value)} className={S}><option value="">All Expense Types</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={filters.status} onChange={(e) => set("status", e.target.value)} className={S}><option value="">All Statuses</option>{["pending", "approved", "rejected", "returned"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <input data-testid="dash-vendor" placeholder="Vendor…" value={filters.vendor} onChange={(e) => set("vendor", e.target.value)} className={S} />
      </div>

      {d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Pending Approvals" value={d.pending_approvals} tone="text-amber-600" />
            <Stat label="Approved This Week" value={money(d.approved_this_week)} tone="text-[#1F3D34]" />
            <Stat label="Reimbursements Pending" value={money(d.reimbursements_pending)} tone="text-amber-600" />
            <Stat label="Reimbursements Paid" value={money(d.reimbursements_paid)} tone="text-emerald-700" />
          </div>
          <div className="flex gap-3">
            <button data-testid="export-xlsx" onClick={() => download("xlsx")} className="h-11 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2"><FileSpreadsheet size={16} /> Excel</button>
            <button data-testid="export-csv" onClick={() => download("csv")} className="h-11 px-4 bg-white border border-stone-300 font-bold uppercase text-xs flex items-center gap-2"><Download size={16} /> CSV</button>
            <div className="ml-auto self-center text-sm text-stone-500">Total: <b className="text-stone-900">{money(d.total)}</b> · {d.count} expenses</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Breakdown title="Total by Ranch" items={d.by_ranch} />
            <Breakdown title="Total by Job Category" items={d.by_category} />
            <Breakdown title="Total by Employee" items={d.by_employee} />
            <Breakdown title="Total by Vendor" items={d.by_vendor} />
          </div>
        </>
      )}
    </div>
  );
}
